import { apiFetch, apiFetchBlob } from "@/lib/api";

const QUOTES_BASE = "/api/inside-sales/quotes";

export type QuoteStatus = "draft" | "pending_approval" | "approved" | "sent" | "expired" | string;

export interface QuoteSummary {
  id: string;
  number: string | null;
  clientName: string | null;
  endUserName: string | null;
  validThrough: string | null;
  createdByDisplayName: string | null;
  status: QuoteStatus;
  total: string;
  updatedAt: string;
  lineCount: number;
  items: Array<Pick<QuoteLine, "id" | "sku" | "description" | "quantity" | "unitPrice" | "pricingType" | "originalPrice" | "total">>;
}

export interface QuoteLine {
  id?: string;
  sku: string;
  description: string;
  quantity: string;
  unitPrice: string;
  pricingType: string;
  originalPrice: string | null;
  minimumQuantity: string | null;
  maximumQuantity: string | null;
  total?: string;
}

export interface QuoteDetail extends QuoteSummary {
  clientId: string | null;
  endUserId: string | null;
  paymentTerm: string;
  comments: string;
  createdByUserId: string | null;
  lines: QuoteLine[];
  warnings?: string[];
}

export interface QuoteListResponse {
  quotes: QuoteSummary[];
  mineOwnerMissing?: boolean;
}

export interface QuoteOption {
  value: string;
  label: string;
  description?: string | null;
}

export interface QuotePricingResponse {
  sku: string;
  description: string | null;
  descriptionError?: string | null;
  unitPrice: string | null;
  originalPrice: string | null;
  pricingType: string | null;
  minimumQuantity: string | null;
  maximumQuantity: string | null;
  warnings: string[];
  blockingError?: string | null;
  pricing?: unknown;
  diagnostics?: {
    parsedSku: Record<string, unknown>;
    rawPriceRowCount: number;
    matchingPriceRowCount: number;
    normalizedTierCount: number;
    rawPriceRows: unknown[];
    matchingPriceRows: unknown[];
  };
}

export interface QuoteWriteInput {
  clientId: string | null;
  endUserId: string | null;
  validThrough: string | null;
  paymentTerm: string;
  comments: string;
  lines: Array<Omit<QuoteLine, "id" | "total">>;
}

export interface QuoteSettings {
  priceLimitMin: number;
  priceLimitMax: number;
  amountLimit: number;
  talkLimit: number;
  updatedAt?: string | null;
}

export const DEFAULT_QUOTE_SETTINGS: QuoteSettings = {
  priceLimitMin: 0.95,
  priceLimitMax: 1.05,
  amountLimit: 50_000,
  talkLimit: 15_000,
};

export const INSIDE_SALES_QUOTES_QUERY_KEY = ["inside-sales", "quotes"] as const;

export function fetchQuotes(mine = false): Promise<QuoteListResponse> {
  return apiFetch<QuoteListResponse>(QUOTES_BASE, { query: { mine: mine ? "1" : undefined } });
}

export function fetchQuote(id: string): Promise<QuoteDetail> {
  return apiFetch<QuoteDetail>(`${QUOTES_BASE}/${encodeURIComponent(id)}`);
}

export function createQuote(input: QuoteWriteInput): Promise<QuoteDetail> {
  return apiFetch<QuoteDetail>(QUOTES_BASE, { method: "POST", body: quoteWriteBody(input) });
}

export function updateQuote(id: string, input: QuoteWriteInput): Promise<QuoteDetail> {
  return apiFetch<QuoteDetail>(`${QUOTES_BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: quoteWriteBody(input),
  });
}

export function duplicateQuote(id: string): Promise<QuoteDetail> {
  return apiFetch<QuoteDetail>(`${QUOTES_BASE}/${encodeURIComponent(id)}/duplicate`, {
    method: "POST",
    body: {},
  });
}

export function deleteQuote(id: string): Promise<void> {
  return apiFetch<void>(`${QUOTES_BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function fetchQuoteClientOptions(): Promise<{ options: QuoteOption[] }> {
  return apiFetch<{ options: QuoteOption[] }>(`${QUOTES_BASE}/options/clients`);
}

export function fetchQuoteEndUserOptions(): Promise<{ options: QuoteOption[] }> {
  return apiFetch<{ options: QuoteOption[] }>(`${QUOTES_BASE}/options/end-users`);
}

export function fetchQuoteSkuOptions(): Promise<{ options: QuoteOption[] }> {
  return apiFetch<{ options: QuoteOption[] }>(`${QUOTES_BASE}/options/skus`);
}

export function fetchQuotePricing(sku: string): Promise<QuotePricingResponse> {
  return apiFetch<QuotePricingResponse>(`${QUOTES_BASE}/pricing`, { query: { sku } });
}

export interface QuoteEndUserQuoteHistoryItem {
  quoteId: number;
  quoteNumber: string;
  validThrough: string | null;
  sku: string;
  unitPrice: number;
}

export interface QuoteEndUserOrderHistoryItem {
  salesOrderId: number;
  salesOrderNumber: string;
  submitted: string | null;
  sku: string;
  unitPrice: number;
}

export function fetchQuoteSettings(): Promise<QuoteSettings> {
  return apiFetch<QuoteSettings>(`${QUOTES_BASE}/settings`);
}

export function fetchQuoteEndUserQuotes(endUserId: string): Promise<{ items: QuoteEndUserQuoteHistoryItem[] }> {
  return apiFetch<{ items: QuoteEndUserQuoteHistoryItem[] }>("/api/sales-orders/end-user-quotes", {
    query: { fsAccountId: endUserId, limit: "50" },
  });
}

export function fetchQuoteEndUserOrders(endUserId: string): Promise<{ items: QuoteEndUserOrderHistoryItem[] }> {
  return apiFetch<{ items: QuoteEndUserOrderHistoryItem[] }>("/api/sales-orders/end-user-history", {
    query: { fsAccountId: endUserId, limit: "60" },
  });
}

export function patchQuoteSettings(settings: QuoteSettings): Promise<QuoteSettings> {
  return apiFetch<QuoteSettings>(`${QUOTES_BASE}/settings`, { method: "PATCH", body: settings });
}

export function quotePdfEndpoint(id: string): string {
  return `${QUOTES_BASE}/${encodeURIComponent(id)}/pdf`;
}

export async function downloadQuotePdf(id: string, filename: string): Promise<void> {
  const result = await apiFetchBlob(quotePdfEndpoint(id));
  const url = URL.createObjectURL(result.blob);
  const opened = window.open(url, "_blank", "noopener");
  if (opened == null) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = result.originalFilename || filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function printQuotePdf(id: string): Promise<void> {
  const result = await apiFetchBlob(quotePdfEndpoint(id));
  const url = URL.createObjectURL(result.blob);
  const opened = window.open(url, "_blank", "noopener");
  if (opened == null) {
    URL.revokeObjectURL(url);
    throw new Error("Allow pop-ups to print the quote PDF.");
  }
  const printWhenReady = () => {
    try {
      opened.focus();
      opened.print();
    } catch {
      // Browser may block print until the PDF viewer finishes loading.
    }
  };
  opened.addEventListener("load", printWhenReady, { once: true });
  window.setTimeout(printWhenReady, 750);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function quoteWriteBody(input: QuoteWriteInput) {
  return {
    ...input,
    lines: input.lines.map(({ pricingType, ...line }) => ({
      ...line,
      type: pricingType,
    })),
  };
}
